<script setup>
/* global axios */
// [AstraChat] Box "Chat Interno" na lateral do ticket: mostra o chat interno vinculado
// (últimas mensagens + responder inline) ou permite vincular um. Espelha o box do Kanban.
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});
const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/internal_chats`;

const chat = ref(null);
const messages = ref([]);
const loading = ref(true);
const reply = ref('');
const sending = ref(false);

const linking = ref(false);
const chatList = ref([]);
const selectedChat = ref(null);
const listBox = ref(null);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(`${base}/for_conversation`, {
      params: { conversation_id: props.conversationId },
    });
    chat.value = data?.data || null;
    messages.value = data?.data?.messages || [];
    await scrollDown();
  } catch (e) {
    chat.value = null;
  } finally {
    loading.value = false;
  }
};

const scrollDown = async () => {
  await nextTick();
  if (listBox.value) listBox.value.scrollTop = listBox.value.scrollHeight;
};

const send = async () => {
  const content = reply.value.trim();
  if (!content || !chat.value) return;
  sending.value = true;
  try {
    const { data } = await axios.post(`${base}/${chat.value.id}/messages`, {
      content,
    });
    messages.value.push(data.data);
    reply.value = '';
    await scrollDown();
  } catch (e) {
    useAlert(t('CHATS.conversationBox.sendError'));
  } finally {
    sending.value = false;
  }
};

const startLink = async () => {
  linking.value = true;
  try {
    const { data } = await axios.get(base);
    chatList.value = data?.data || [];
  } catch (e) {
    chatList.value = [];
  }
};

const confirmLink = async () => {
  if (!selectedChat.value) return;
  try {
    await axios.patch(`${base}/${selectedChat.value}/link`, {
      conversation_id: props.conversationId,
    });
    linking.value = false;
    selectedChat.value = null;
    await load();
    useAlert(t('CHATS.conversationBox.linked'));
  } catch (e) {
    useAlert(t('CHATS.conversationBox.linkError'));
  }
};

const unlink = async () => {
  if (!chat.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm('Desvincular este chat interno do ticket?')) return;
  try {
    await axios.delete(`${base}/${chat.value.id}/unlink`, {
      data: { conversation_id: props.conversationId },
    });
    chat.value = null;
    messages.value = [];
  } catch (e) {
    useAlert(t('CHATS.conversationBox.unlinkError'));
  }
};

const openInInternalChats = () => {
  router.push({ name: 'kanban_internal_chats', query: { chat: chat.value?.id } });
};

const fmtTime = iso => {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return '';
  }
};

// N:N: qualquer chat pode receber o ticket (um chat tem vários tickets).
const availableChats = computed(() => chatList.value);

onMounted(load);
</script>

<template>
  <div class="px-1 py-1">
    <p v-if="loading" class="py-3 text-xs text-center text-n-slate-11">
      {{ t('CHATS.conversationBox.loading') }}
    </p>

    <!-- Vinculado: conversa + responder -->
    <div v-else-if="chat" class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <span class="i-lucide-messages-square size-4 text-n-slate-11 shrink-0" />
        <span class="text-sm font-medium truncate text-n-slate-12 flex-1">{{
          chat.name
        }}</span>
        <Button
          v-tooltip.top="t('CHATS.conversationBox.openInChat')"
          icon="i-lucide-external-link"
          slate
          xs
          faded
          @click="openInInternalChats"
        />
        <Button
          v-tooltip.top="t('CHATS.conversationBox.unlink')"
          icon="i-lucide-unlink"
          slate
          xs
          faded
          @click="unlink"
        />
      </div>

      <div
        ref="listBox"
        class="flex flex-col gap-2 px-1 py-2 overflow-y-auto rounded-lg max-h-56 bg-n-alpha-1"
      >
        <p v-if="!messages.length" class="text-xs text-center text-n-slate-10 py-4">
          {{ t('CHATS.conversationBox.empty') }}
        </p>
        <div v-for="m in messages" :key="m.id" class="flex flex-col">
          <div class="flex items-baseline gap-2">
            <span class="text-[0.6875rem] font-medium text-n-slate-11">{{
              m.user_name
            }}</span>
            <span class="text-[0.625rem] text-n-slate-10">{{
              fmtTime(m.created_at)
            }}</span>
          </div>
          <span class="text-sm text-n-slate-12 break-words whitespace-pre-wrap">{{
            m.content
          }}</span>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <input
          v-model="reply"
          type="text"
          :placeholder="t('CHATS.conversationBox.replyPlaceholder')"
          class="flex-1 px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          @keydown.enter="send"
        />
        <Button
          icon="i-lucide-send"
          sm
          :disabled="sending || !reply.trim()"
          @click="send"
        />
      </div>
    </div>

    <!-- Não vinculado -->
    <div v-else class="flex flex-col gap-2 py-1">
      <template v-if="!linking">
        <p class="text-xs text-n-slate-11">
          {{ t('CHATS.conversationBox.none') }}
        </p>
        <Button
          :label="t('CHATS.conversationBox.link')"
          icon="i-lucide-link"
          slate
          faded
          sm
          @click="startLink"
        />
      </template>
      <template v-else>
        <select
          v-model="selectedChat"
          class="px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
        >
          <option :value="null" disabled>
            {{ t('CHATS.conversationBox.pickChat') }}
          </option>
          <option v-for="c in availableChats" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
        <div class="flex gap-2">
          <Button label="Cancelar" slate faded sm @click="linking = false" />
          <Button label="Vincular" sm :disabled="!selectedChat" @click="confirmLink" />
        </div>
      </template>
    </div>
  </div>
</template>
